import React from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft, Scroll, Heart, MessageSquareWarning, ShieldAlert, UserX,
  Megaphone, Ban, Flag, Sparkles, Crown,
} from "lucide-react";
import Sidebar from "@/components/Sidebar";
import Header from "@/components/Header";
import useSeo from "@/hooks/useSeo";
import Footer from "@/components/Footer";

const RULES = [
  {
    Icon: Heart,
    title: "Be human, be kind",
    body:
      "We're a community of movie nerds — not a battleground. Disagree on a film's rating, debate codec preferences, but never attack the person. Hate speech, slurs, harassment and personal attacks earn an instant ban.",
    accent: "#FF2A6D",
  },
  {
    Icon: MessageSquareWarning,
    title: "Stay on-topic",
    body:
      "Comments belong on the title they discuss. Off-topic rants, copy-pasted spam and one-word replies dilute the conversation and tank your karma. Posts that contribute nothing get cleaned up by mods.",
    accent: "#FFD700",
  },
  {
    Icon: Megaphone,
    title: "No advertising, no referral spam",
    body:
      "Don't drop affiliate links, crypto pitches, OnlyFans plugs, or referral codes into comments. TorHub clearly discloses its own affiliate partners on the Software page — anything else is treated as spam.",
    accent: "#FF6B35",
  },
  {
    Icon: UserX,
    title: "One account per human",
    body:
      "No sock-puppets, no upvote rings, no farming karma with alt accounts. We detect duplicate sessions and ban every linked account permanently — your karma vanishes with them.",
    accent: "#6D4AFF",
  },
  {
    Icon: ShieldAlert,
    title: "Don't dox, don't share private info",
    body:
      "Real names, addresses, phone numbers, employer details, screenshots of private DMs — none of it belongs here. Doxxing is an instant permaban and we'll cooperate with law enforcement if asked.",
    accent: "#EF4444",
  },
  {
    Icon: Flag,
    title: "Report, don't fight",
    body:
      "See a broken torrent, a malicious link, or a comment that crosses the line? Use the report button instead of starting a flame war. Our mod team reviews every report and acts within hours.",
    accent: "#22C77A",
  },
  {
    Icon: Ban,
    title: "What gets you banned, fast",
    body:
      "CSAM, malware, doxxing, repeated harassment, ban evasion, and posting magnets for personal/leaked content (revenge leaks, private docs, anything non-public-domain). Zero warnings, zero appeals.",
    accent: "#DC143C",
  },
  {
    Icon: Sparkles,
    title: "Earn your crown",
    body:
      "Quality posts compound. A single insightful comment can earn more karma than a dozen one-liners — and karma turns into Hall-of-Fame appearances, custom flair, and bragging rights. Be the user other users want to read.",
    accent: "#2BD9A5",
  },
];

export default function RulesPage() {
  useSeo({
    title: "Community Rules — How to behave on TorHub",
    description:
      "TorHub's community rules: be kind, stay on-topic, no advertising, one account per human, no doxxing, report bad actors. Everything you need to know to stay in good standing.",
    path: "/rules",
  });

  return (
    <div className="flex h-screen overflow-hidden bg-black text-white">
      <Sidebar />
      <div className="flex-1 h-full overflow-y-auto flex flex-col">
        <Header />
        <main className="px-4 md:px-10 py-8 flex-1 w-full max-w-4xl mx-auto">
          <Link
            to="/"
            data-testid="rules-back-home"
            className="text-[var(--tt-text-muted)] hover:text-white flex items-center gap-2 text-sm mb-6"
          >
            <ArrowLeft className="w-4 h-4" /> Back
          </Link>

          <header className="mb-10">
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] text-[var(--tt-primary)] font-bold mb-3">
              <Scroll className="w-3.5 h-3.5" />
              House Rules
            </div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-[0.95] tracking-tight">
              Don&apos;t be
              <br />
              <span className="text-[var(--tt-primary)]">that user.</span>
            </h1>
            <p className="text-sm md:text-base text-[var(--tt-text-muted)] mt-5 max-w-2xl leading-relaxed">
              TorHub is a movie and TV community first, a magnet aggregator second. These are the rules that
              keep it that way — short, honest, and enforced consistently by mods. By creating an account, you
              agree to abide by everything below.
            </p>
            <div className="mt-5 text-[11px] uppercase tracking-[0.18em] text-[var(--tt-text-faint)] font-mono-tt">
              Last updated · February 2026
            </div>
          </header>

          <ol className="space-y-3" data-testid="rules-list">
            {RULES.map((rule, idx) => (
              <li
                key={rule.title}
                data-testid={`rules-item-${idx}`}
                className="group relative rounded-3xl border border-[var(--tt-border)] bg-[var(--tt-surface)]/70 backdrop-blur-md p-5 md:p-6 transition-all hover:-translate-y-0.5 hover:border-[var(--tt-primary)]/40 overflow-hidden"
              >
                <div
                  className="absolute -top-16 -right-16 w-40 h-40 rounded-full opacity-25 blur-3xl pointer-events-none"
                  style={{ background: rule.accent }}
                />
                <div className="relative flex items-start gap-4">
                  <div className="flex-shrink-0 hidden sm:flex flex-col items-center gap-2">
                    <div
                      className="w-12 h-12 rounded-2xl flex items-center justify-center"
                      style={{
                        background: `linear-gradient(135deg, ${rule.accent}33, transparent 60%), #0b0b0b`,
                        boxShadow: `inset 0 0 0 1px ${rule.accent}55`,
                      }}
                    >
                      <rule.Icon className="w-5 h-5" style={{ color: rule.accent }} />
                    </div>
                    <span className="font-mono-tt text-[10px] text-[var(--tt-text-muted)]">
                      {String(idx + 1).padStart(2, "0")}
                    </span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <h2 className="font-display text-lg md:text-xl font-extrabold tracking-tight">
                      {rule.title}
                    </h2>
                    <p className="text-sm text-white/80 leading-relaxed mt-2">{rule.body}</p>
                  </div>
                </div>
              </li>
            ))}
          </ol>

          {/* Mod actions footer */}
          <section className="mt-10 rounded-3xl border border-[var(--tt-border)] bg-gradient-to-br from-[rgba(255,42,109,0.06)] via-transparent to-[rgba(255,215,0,0.04)] backdrop-blur-md p-6 md:p-8">
            <div className="flex items-start gap-4">
              <div className="hidden md:flex w-12 h-12 rounded-2xl bg-[var(--tt-primary)]/15 border border-[var(--tt-primary)]/30 items-center justify-center flex-shrink-0">
                <Crown className="w-5 h-5 text-[var(--tt-primary)]" />
              </div>
              <div className="flex-1">
                <h3 className="font-display text-lg md:text-xl font-extrabold mb-2">
                  How enforcement works
                </h3>
                <p className="text-sm text-white/75 leading-relaxed">
                  Mods can delete comments, reclassify torrents, ban accounts and revert one another&apos;s
                  actions — every moderator action is logged in a public audit trail visible to the admin team.
                  Severe violations skip warnings and result in an instant permanent ban; everything else gets a
                  first-offence cleanup with a polite DM. Appeals are reviewed by an admin within 72 hours.
                </p>
                <div className="mt-4 flex flex-wrap gap-2">
                  <Link
                    to="/disclaimer"
                    data-testid="rules-to-disclaimer"
                    className="inline-flex items-center gap-2 text-[11px] uppercase tracking-wider font-semibold text-white/70 hover:text-white px-3 py-1.5 rounded-full bg-white/5 border border-white/10 hover:border-white/30"
                  >
                    Read the Disclaimer
                  </Link>
                  <Link
                    to="/hall-of-fame"
                    data-testid="rules-to-hof"
                    className="inline-flex items-center gap-2 text-[11px] uppercase tracking-wider font-semibold text-white/70 hover:text-white px-3 py-1.5 rounded-full bg-white/5 border border-white/10 hover:border-white/30"
                  >
                    See the Hall of Fame
                  </Link>
                </div>
              </div>
            </div>
          </section>
        </main>
        <Footer />
      </div>
    </div>
  );
}
